import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';

const solutions = [
  {
    tag: 'Fintech & Payments',
    title: 'Enterprise Billing & Subscription Engine',
    desc: 'Full-featured billing platform supporting global payment gateways, multi-tier subscription lifecycles, automated invoicing, plan renewals, and intelligent dunning recovery.',
    image: '/solutions/billing-platform.jpg',
  },
  {
    tag: 'Computer Vision',
    title: 'AI Photo Recognition Platform',
    desc: 'Instant facial recognition and smart auto-tagging for massive event photo libraries at enterprise scale.',
    image: '/solutions/ai-vision.jpg',
  },
  {
    tag: 'Autonomous AI',
    title: 'Multi-Agent Workforce Platform',
    desc: 'Self-orchestrating swarm of specialized autonomous AI agents handling customer intelligence, automated analytics, and mission-critical workflows.',
    image: '/solutions/ai-agents.jpg',
  },
  {
    tag: 'Growth Engineering',
    title: 'AI Growth & Digital Marketing Suite',
    desc: 'Automated omnichannel marketing engine with real-time viral growth radar, predictive conversion funnels, and programmatic SEO orchestration.',
    image: '/solutions/digital-marketing.jpg',
  },
  {
    tag: 'SaaS Platform',
    title: 'Event Tech System',
    desc: 'End-to-end ticketing, live QR scanning, capacity analytics, and real-time dashboards for large-scale events.',
    image: '/solutions/event-tech.jpg',
  },
  {
    tag: 'API Infrastructure',
    title: 'API-as-a-Service Platform',
    desc: 'High-throughput RESTful and GraphQL microservices allowing automated global data exchange at massive volumes.',
    image: '/solutions/api-infra.jpg',
  },
  {
    tag: 'B2C Ticketing SaaS',
    title: 'MasseShots Club & Bar Booking',
    desc: 'A complete custom ticketing and table reservation engine like BookMyShow, engineered specifically for pubs, lounges, bars, and gaming clubs. Features dynamic ticket allocations and real-time pass validation.',
    image: '/solutions/club-booking.jpg',
  },
  {
    tag: 'Workflow Intelligence',
    title: 'Enterprise Automation Tools',
    desc: 'Intelligent process automation replacing manual workflows with fast, secure, and observable automated logic.',
    image: '/solutions/automation.jpg',
  },
];

const Solutions = () => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    const cardW = isMobile ? el.clientWidth * 0.88 + 16 : 504;
    setActiveIndex(Math.round(el.scrollLeft / cardW));
  }, [isMobile]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = isMobile ? el.clientWidth * 0.88 + 16 : 520;
    el.scrollBy({ left: dir === 'left' ? -cardW : cardW, behavior: 'smooth' });
    setTimeout(updateScrollState, 400);
  };

  const scrollToIndex = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = isMobile ? el.clientWidth * 0.88 + 16 : 504;
    el.scrollTo({ left: i * cardW, behavior: 'smooth' });
    setTimeout(updateScrollState, 400);
  };

  // Mouse drag
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollLeft = 0;

    const onDown  = (e) => { isDown = true; setIsDragging(false); startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; };
    const onLeave = ()  => { isDown = false; el.style.cursor = 'grab'; };
    const onUp    = ()  => { isDown = false; el.style.cursor = 'grab'; setTimeout(() => setIsDragging(false), 50); };
    const onMove  = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const walk = (e.pageX - el.offsetLeft - startX) * 1.5;
      if (Math.abs(walk) > 5) setIsDragging(true);
      el.scrollLeft = scrollLeft - walk;
      updateScrollState();
    };

    el.addEventListener('mousedown', onDown);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('mouseup', onUp);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('scroll', updateScrollState);
    return () => {
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('mouseup', onUp);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [isMobile, updateScrollState]);

  const cardW    = isMobile ? '86vw'  : '460px';
  const cardMinW = isMobile ? '86vw'  : '460px';
  const cardH    = isMobile ? '460px' : '460px';
  const cardP    = isMobile ? '28px 24px' : '36px 32px';
  const titleSz  = isMobile ? '22px'  : '26px';
  const descSz   = isMobile ? '13.5px' : '14.5px';
  const arrowSz  = isMobile ? '40px'  : '52px';
  const padLeft  = isMobile
    ? '20px'
    : 'max(40px, calc((100vw - 1320px) / 2 + 40px))';

  return (
    <section
      id="solutions"
      ref={containerRef}
      style={{ padding: isMobile ? '40px 0' : '60px 0', background: 'transparent', overflow: 'hidden' }}
    >
      {/* ── Header row ── */}
      <div style={{
        maxWidth: '1320px',
        margin: '0 auto',
        padding: isMobile ? '0 20px' : '0 40px',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: isMobile ? '32px' : '48px',
        flexWrap: 'wrap',
        gap: '20px',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="section-label">Solutions</p>
          <h2 className="section-title" style={{ marginBottom: '10px' }}>
            System Blueprints.
          </h2>
          <p className="section-subtitle">
            {isMobile
              ? 'Swipe to explore architectures we\'ve deployed.'
              : 'Architectures we\'ve designed and deployed globally. Drag or use arrows to explore.'}
          </p>
        </motion.div>

        {/* Arrow buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ display: 'flex', gap: '10px', flexShrink: 0 }}
        >
          {['left', 'right'].map((dir) => {
            const active = dir === 'left' ? canScrollLeft : canScrollRight;
            return (
              <button
                key={dir}
                onClick={() => scroll(dir)}
                disabled={!active}
                aria-label={`Scroll ${dir}`}
                style={{
                  width: arrowSz, height: arrowSz,
                  borderRadius: '50%',
                  border: `1.5px solid ${active ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  background: active ? '#ffffff' : 'rgba(255,255,255,0.03)',
                  color: active ? '#000000' : 'rgba(255,255,255,0.2)',
                  fontSize: isMobile ? '16px' : '20px',
                  cursor: active ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  flexShrink: 0,
                }}
              >
                {dir === 'left' ? '←' : '→'}
              </button>
            );
          })}
        </motion.div>
      </div>

      {/* ── Scroll track ── */}
      <div
        ref={trackRef}
        style={{
          display: 'flex',
          gap: isMobile ? '16px' : '20px',
          paddingLeft: padLeft,
          paddingRight: isMobile ? '20px' : '40px',
          paddingBottom: '8px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          cursor: 'grab',
          userSelect: 'none',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x mandatory',
        }}
      >
        <style>{`
          #solutions-track::-webkit-scrollbar { display: none; }
        `}</style>

        {solutions.map((s, i) => (
          <motion.div
            key={s.title}
            className="solution-card"
            initial={{ opacity: 0, y: 30 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
            style={{
              width: cardW,
              minWidth: cardMinW,
              height: cardH,
              minHeight: isMobile ? '420px' : '460px',
              borderRadius: isMobile ? '24px' : '28px',
              padding: cardP,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              position: 'relative',
              overflow: 'hidden',
              cursor: isDragging ? 'grabbing' : 'pointer',
              background: '#06080e',
              border: '1px solid rgba(255,255,255,0.08)',
              flexShrink: 0,
              scrollSnapAlign: 'start',
              transition: 'box-shadow 0.4s, border-color 0.4s',
              boxShadow: activeIndex === i
                ? '0 20px 60px rgba(0,0,0,0.4), 0 0 30px rgba(20,184,166,0.1)'
                : '0 8px 32px rgba(0,0,0,0.3)',
            }}
            whileHover={!isDragging && !isMobile ? {
              y: -8,
              boxShadow: '0 30px 80px rgba(0,0,0,0.5), 0 0 40px rgba(20,184,166,0.15)',
              borderColor: 'rgba(255,255,255,0.2)',
            } : {}}
          >
            {/* Background AI Blueprint Image with Gradient Overlay */}
            <div style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}>
              <img
                src={s.image}
                alt={s.title}
                loading="lazy"
                className="solution-card-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 15%',
                  filter: 'brightness(0.75) contrast(1.15)',
                  transition: 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1), filter 0.7s ease',
                }}
              />
              {/* Seamless atmospheric gradient ensuring text readability */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(6, 8, 14, 0.1) 0%, rgba(6, 8, 14, 0.45) 40%, rgba(6, 8, 14, 0.92) 75%, #06080e 100%)',
              }} />
            </div>

            {/* Content overlay */}
            <div style={{ position: 'relative', zIndex: 2 }}>
              <span style={{
                display: 'inline-block',
                padding: isMobile ? '5px 14px' : '6px 16px',
                borderRadius: '100px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(10, 15, 28, 0.75)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                fontSize: isMobile ? '10px' : '11px',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#ffffff',
                marginBottom: isMobile ? '12px' : '16px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}>
                {s.tag}
              </span>

              <h3 style={{
                fontSize: titleSz,
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: '#ffffff',
                marginBottom: isMobile ? '10px' : '12px',
                lineHeight: 1.2,
                textShadow: '0 2px 10px rgba(0,0,0,0.5)',
              }}>
                {s.title}
              </h3>

              <p style={{
                fontSize: descSz,
                fontWeight: 400,
                color: 'rgba(255,255,255,0.75)',
                lineHeight: 1.6,
                maxWidth: '400px',
                textShadow: '0 1px 6px rgba(0,0,0,0.6)',
              }}>
                {s.desc}
              </p>
            </div>
          </motion.div>
        ))}

        {/* End spacer */}
        <div style={{ minWidth: isMobile ? '4px' : '20px', flexShrink: 0 }} />
      </div>

      {/* ── Dot indicators ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginTop: isMobile ? '28px' : '36px',
        paddingBottom: '4px',
      }}>
        {solutions.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            style={{
              width: activeIndex === i ? '20px' : '6px',
              height: '6px',
              borderRadius: '3px',
              border: 'none',
              background: activeIndex === i ? '#ffffff' : 'rgba(255,255,255,0.2)',
              cursor: 'pointer',
              padding: 0,
              transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default Solutions;
